import { attributes } from '../../core/ids-attributes';
import { IdsConstructor } from '../../core/ids-element';

/**
 * Label position options for form components
 * - 'top': Label appears above the input (default)
 * - 'inline-start': Label appears at the start (left in LTR, right in RTL)
 * - 'inline-end': Label appears at the end (right in LTR, left in RTL)
 */
export type IdsLabelPosition = 'top' | 'inline-start' | 'inline-end';

/**
 * Label break options for form components
 * - 'shrink-value': Label stays on the same line; the value area shrinks when space is constrained (default)
 * - 'break': The value area moves below the label when space is constrained
 */
export type IdsLabelBreak = 'shrink-value' | 'break';

/**
 * Label alignment options for form components
 * - 'start': Label text aligns to the start (default)
 * - 'end': Label text aligns to the end
 */
export type IdsLabelAlignment = 'start' | 'end';

/**
 * Label wrap options for form components
 * - 'wrap': Label text wraps to multiple lines (default)
 * - 'ellipsis': Label text is truncated with ellipsis and shows tooltip on hover
 * - 'wrap-no-stretch': Label text wraps but doesn't stretch the container
 * - 'ellipsis-no-stretch': Label text is truncated but doesn't stretch the container
 */
export type IdsLabelWrap = 'ellipsis' | 'wrap' | 'ellipsis-no-stretch' | 'wrap-no-stretch';

const LABEL_WRAPS = ['ellipsis', 'wrap', 'ellipsis-no-stretch', 'wrap-no-stretch'];

const LABEL_ALIGNMENTS = ['start', 'end'];

/**
 * Interface for the IdsLabelPositionMixin
 */
export interface IdsLabelPositionMixinInterface {
  /** The position of the label relative to the input */
  labelPosition: string | null;
  /** The width of the label when using inline positioning */
  labelWidth: string | null;
  /** The break behavior of the label and value */
  labelBreak: string | null;
  /** The wrap behavior of the label text */
  labelWrap: string | null;
}

/**
 * A mixin that adds label positioning capabilities to form components
 * @mixin IdsLabelPositionMixin
 * @param {any} superclass The class to extend
 * @returns {any} The extended class
 */
const IdsLabelPositionMixin = <T extends IdsConstructor<any>>(superclass: T) => class extends superclass {
  /**
   * Watches for resizing that occurs whenever the page changes dimensions
   * @property {ResizeObserver} mo this component's resize observer
   */
  labelMixinRo?: ResizeObserver;

  /**
   * Watches for changes
   * @property {MutationObserver} mo this component's mutation observer
   */
  labelMixinMo?: MutationObserver;

  hasCustomTooltip: boolean = false;

  constructor(...args: any[]) {
    super(...args);
  }

  connectedCallback() {
    super.connectedCallback?.();
    this.initMutationObserver();
    this.initResizeObserver();

    // Determine if a custom tooltip was explicitly set (different from label)
    const tooltipAttr = this.getAttribute(attributes.TOOLTIP);
    const labelAttr = this.getAttribute(attributes.LABEL);
    this.hasCustomTooltip = !!(tooltipAttr && tooltipAttr !== 'true' && tooltipAttr !== labelAttr);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.destroyResizeObserver();
    this.destroyMutationObserver();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.LABEL_ALIGN_Y,
      attributes.LABEL_ALIGNMENT,
      attributes.LABEL_BREAK,
      attributes.LABEL_POSITION,
      attributes.LABEL_WIDTH,
      attributes.LABEL_WRAP,
      attributes.LINE_CLAMP,
    ];
  }

  /**
   * Get the label position
   * @returns {IdsLabelPosition|null} The current label position
   */
  get labelPosition(): IdsLabelPosition | null {
    return (this.getAttribute(attributes.LABEL_POSITION) as IdsLabelPosition) || 'top';
  }

  /**
   * Set the label position
   * @param {IdsLabelPosition|null} value The label position to set
   */
  set labelPosition(value: IdsLabelPosition | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_POSITION, value);
    } else {
      this.removeAttribute(attributes.LABEL_POSITION);
    }

    this.initMutationObserver();
    this.initResizeObserver();

    // Microqueue the callback to allow the component to update first
    // eslint-disable-next-line no-void
    void Promise.resolve().then(() => {
      if (typeof this.onLabelPositionChange === 'function') {
        this.onLabelPositionChange();
      }
    });
  }

  /**
   * Get the label width
   * @returns {string|null} The current label width
   */
  get labelWidth(): string | null {
    return this.getAttribute(attributes.LABEL_WIDTH);
  }

  /**
   * Set the label width
   * @param {string|null} value The label width to set (e.g., '120px', 'auto')
   */
  set labelWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_WIDTH, value);
      this.style.setProperty('--ids-label-width', value);
    } else {
      this.removeAttribute(attributes.LABEL_WIDTH);
      this.style.removeProperty('--ids-label-width');
    }
    if (typeof this.onLabelWidthChange === 'function') {
      this.onLabelWidthChange();
    }
  }

  /**
   * Get the vertical alignment of the label
   * @returns {string|null} The current vertical alignment ('baseline', 'center', 'flex-start', 'flex-end', 'stretch')
   */
  get labelAlignY(): string | null {
    return this.getAttribute(attributes.LABEL_ALIGN_Y) || 'center';
  }

  /**
   * Set the vertical alignment of the label
   * @param {string|null} value The vertical alignment to set ('baseline', 'center', 'flex-start', 'flex-end', 'stretch')
   */
  set labelAlignY(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_ALIGN_Y, value);
      this.style.setProperty('--ids-label-align-y', value);
    } else {
      this.removeAttribute(attributes.LABEL_ALIGN_Y);
      this.style.removeProperty('--ids-label-align-y');
    }
    if (typeof this.onLabelAlignYChange === 'function') {
      this.onLabelAlignYChange();
    }
  }

  /**
   * Get the horizontal text alignment of the label
   * @returns {IdsLabelAlignment|null} The current label alignment
   */
  get labelAlignment(): IdsLabelAlignment | null {
    return (this.getAttribute(attributes.LABEL_ALIGNMENT) as IdsLabelAlignment) || null;
  }

  /**
   * Set the horizontal text alignment of the label
   * @param {IdsLabelAlignment|null} value The label alignment to set ('start' or 'end')
   */
  set labelAlignment(value: IdsLabelAlignment | null) {
    if (!value || LABEL_ALIGNMENTS.indexOf(value as any) < 0) {
      this.removeAttribute(attributes.LABEL_ALIGNMENT);
      this.style.removeProperty('--ids-label-alignment');
      if (typeof this.onLabelAlignmentChange === 'function') {
        this.onLabelAlignmentChange();
      }
      return;
    }
    this.setAttribute(attributes.LABEL_ALIGNMENT, value);
    this.style.setProperty('--ids-label-alignment', value);
    if (typeof this.onLabelAlignmentChange === 'function') {
      this.onLabelAlignmentChange();
    }
  }

  /**
   * Get the label break behavior
   * @returns {IdsLabelBreak|null} The current label break behavior
   */
  get labelBreak(): IdsLabelBreak | null {
    return (this.getAttribute(attributes.LABEL_BREAK) as IdsLabelBreak) || 'shrink-value';
  }

  /**
   * Set the label break behavior
   * @param {IdsLabelBreak|null} value The label break behavior to set
   */
  set labelBreak(value: IdsLabelBreak | null) {
    const currentValue = (this.getAttribute(attributes.LABEL_BREAK) as IdsLabelBreak) || 'shrink-value';

    if (value) {
      this.setAttribute(attributes.LABEL_BREAK, value);
    } else {
      this.removeAttribute(attributes.LABEL_BREAK);
    }

    // Update CSS classes if the value changed
    if (currentValue !== value) {
      this.onLabelBreakChange?.(value, currentValue);
    }
  }

  /**
   * Get the label wrap behavior
   * @returns {IdsLabelWrap|null} The current label wrap behavior
   */
  get labelWrap(): IdsLabelWrap | null {
    return (this.getAttribute(attributes.LABEL_WRAP) as IdsLabelWrap) || 'wrap';
  }

  /**
   * Set the label wrap behavior
   * @param {IdsLabelWrap|null} value The label wrap behavior to set
   */
  set labelWrap(value: IdsLabelWrap | null) {
    if (!value || LABEL_WRAPS.indexOf(value as any) < 0) {
      this.removeAttribute(attributes.LABEL_WRAP);
      if (this.labelTextEl) {
        this.labelTextEl.removeAttribute(attributes.LABEL_WRAP);
      }
      return;
    }

    this.setAttribute(attributes.LABEL_WRAP, value);
    if (this.labelTextEl) {
      this.labelTextEl.setAttribute(attributes.LABEL_WRAP, value);
    }
  }

  /**
   * Update tooltip on label text element based on current label and labelWrap settings
   * @private
   */
  updateLabelTooltip() {
    if (this.hasCustomTooltip) {
      return;
    }

    if (this.labelTextEl) {
      if ((this.labelWrap?.includes('ellipsis') || this.lineClamp) && this.label) {
        this.labelTextEl.setAttribute(attributes.TOOLTIP, this.label);
      } else {
        this.labelTextEl.removeAttribute(attributes.TOOLTIP);
      }
    }
  }

  /**
   * Set the line clamp value for the label text
   * @param {string | number | null} value The number of lines to clamp
   */
  set lineClamp(value: string | number | null) {
    if (value) {
      this.setAttribute(attributes.LINE_CLAMP, `${value}`);
    } else {
      this.removeAttribute(attributes.LINE_CLAMP);
    }

    if (this.labelTextEl) {
      if (value) {
        this.labelTextEl.setAttribute(attributes.LINE_CLAMP, `${value}`);
      } else {
        this.labelTextEl.removeAttribute(attributes.LINE_CLAMP);
      }
    }

    this.updateLabelTooltip();
  }

  /**
   * Get the line clamp value
   * @returns {string | null} the line clamp value
   */
  get lineClamp(): string | null {
    return this.getAttribute(attributes.LINE_CLAMP);
  }

  /**
   * Setup ResizeObserver to detect validation message wrapping
   * @returns {void}
   */
  initResizeObserver(): void {
    const container = this.labelContainer?.();

    if (this.labelPosition !== 'inline-start') {
      this.destroyResizeObserver();
      return;
    }

    if (this.labelMixinRo || !container) {
      return;
    }
    this.labelMixinRo = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.onContainerChange(container);
      });
    });

    // typed as any because this is a mixin not inheriting from IdsElement
    this.labelMixinRo.observe(this as any);
  }

  initMutationObserver(): void {
    const container = this.labelContainer?.();

    if (this.labelPosition !== 'inline-start') {
      this.destroyMutationObserver();
      return;
    }

    if (this.labelMixinMo || !container || this.labelPosition !== 'inline-start') {
      return;
    }
    this.labelMixinMo = new MutationObserver(() => {
      requestAnimationFrame(() => {
        this.onContainerChange(container);
      });
    });

    // typed as any because this is a mixin not inheriting from IdsElement
    this.labelMixinMo.observe(container as any, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Destroys the ResizeObserver instance and cleans up resources
   * @returns {void}
   */
  destroyResizeObserver() {
    if (!this.labelMixinRo) {
      return;
    }

    this.labelMixinRo.disconnect();
    this.labelMixinRo = undefined;
  }

  /**
   * Destroys the MutationObserver instance and cleans up resources
   * @returns {void}
   */
  destroyMutationObserver() {
    if (!this.labelMixinMo) {
      return;
    }

    this.labelMixinMo.disconnect();
    this.labelMixinMo = undefined;
  }

  onContainerChange(container: HTMLElement) {
    if (container) {
      const label = container.querySelector('label');
      const fieldContainer = container.querySelector<HTMLElement>('.field-container');
      const validationMessage = container.querySelector('.validation-message');

      if (label && fieldContainer) {
        const fieldTop = fieldContainer.offsetTop;
        const labelStyles = getComputedStyle(label);
        const calculatedLabelHeight = label.offsetHeight
          + parseFloat(labelStyles.marginTop)
          + parseFloat(labelStyles.marginBottom);
        if (fieldTop === 0 || calculatedLabelHeight > fieldTop || container.offsetTop === fieldTop) {
          if (validationMessage) container.classList.add('wrapped');
        } else {
          container.classList.remove('wrapped');
        }
      }
    }
  }

  /**
   * Gets the label container element from the shadow DOM
   * NOTE: This method can be overridden in concrete classes (e.g. IdsInput/IdsTextarea) for special cases or variations
   * @returns {HTMLElement|null} The container element with inline-start label position, or null if not found
   */
  labelContainer(): HTMLElement | null {
    return this.shadowRoot?.querySelector('[part="container"] [label-position="inline-start"]')?.shadowRoot?.querySelector('.ids-input, .ids-textarea, .ids-checkbox');
  }
};

export default IdsLabelPositionMixin;
